import React from 'react'
import * as AppStore from './../../state/app-store'
import { Breadcrumbs, Title } from '@mantine/core'
// @ts-ignore
import iso3311a2 from 'iso-country-lookup'
import pluralize from 'pluralize'
import Link from 'next/link'

import HighLevelSummary from './HighLevelSummary'
import TopImageGallery from './TopImageGallery'
import RemainingImagesGallery from './RemainingImagesGallery'
import ImageModal from './components/ImageModal'
import TableOfContents from './components/TableOfContents'
import CountryBadge from '../../components/CountryBadge'
import * as Types from '../../declarations'
import {
	distanceOrHeight as getDistanceOrHeight,
	getContinentRoute,
	getCountryRoute,
	lengthInDays as getLengthInDays,
} from '../../lib/display'
import { sluggify } from '../../lib/helper'
import * as Selectors from './../../state/selectors'
import HTMLContent from 'components/HTMLContent'

interface Props {
	trip: Types.DB.Models.TripAttributes
}

const Overview = ({
	trip: {
		name,
		jsonData: {
			alternativeNames = [],
			description,
			// highLevelInfo,
			countries,
			// continent,
			// slug,
			trekOrClimb,
		},
		uploads: allImages,
	},
	trip,
}: Props) => {
	const unit = AppStore.useAppSelector(Selectors.unitSelector)
	const isTrekNotPeak = trekOrClimb === 'TREK'

	const [lightboxIndex, setLightboxIndex] = React.useState(-1)

	const images =
		allImages?.filter(({ isDisplayedInGallery }) => isDisplayedInGallery) ?? []
	// const lightboxImages = images.map(({ id }) =>
	// 	getUploadThumbPath(id, 'large'),
	// )

	const lightboxThumbClicked = (idClicked: number) => {
		const imgKey = images.findIndex(({ id }) => id === idClicked)
		setLightboxIndex(imgKey)
	}

	const distanceOrHeight = getDistanceOrHeight(trip, unit)

	const lengthInDays = getLengthInDays(trip)

	const items = [
		{
			title: `All ${trekOrClimb === 'TREK' ? 'Treks' : 'Climbing expeditions'}`,
			href: '/expeditions',
		},
		{
			title: trip.jsonData?.continent ?? 'no continent',
			href: getContinentRoute(trekOrClimb, trip.jsonData.continent),
		},
		...(trip.jsonData?.countries?.[0]
			? [
					{
						title: sluggify(iso3311a2.getCountry(trip.jsonData.countries[0])),
						href: getCountryRoute(
							trekOrClimb,
							trip.jsonData.continent,
							trip.jsonData.countries[0],
						),
					},
			  ]
			: []),
	]
		.filter((item) => !!item)
		.map((item, index) => (
			<Link
				href={item.href}
				key={index}
				style={{ textTransform: 'capitalize' }}
			>
				{item.title}
			</Link>
		))

	return (
		<div id="overview-wrapper">
			<ImageModal
				activeImageIndex={lightboxIndex}
				images={images}
				changeActiveImageIndex={setLightboxIndex}
			/>

			<Title order={1}>
				{isTrekNotPeak ? 'Treking' : 'Climbing'} {name}
			</Title>
			{alternativeNames.length > 0 && (
				<Title order={2} id="also-known-as-header">
					Also known as: <strong>{alternativeNames.join(' / ')}</strong>
				</Title>
			)}
			<div id="breadcrumbs">
				<Breadcrumbs>{items}</Breadcrumbs>
			</div>
			<div id="trek-one-line-summary">
				{/* // todo: show total gain here if a climb */}
				{distanceOrHeight && (
					<div className="one-line-summary-item">
						{isTrekNotPeak ? 'Distance' : 'Height'}:{' '}
						<strong>{distanceOrHeight}</strong>
					</div>
				)}
				{lengthInDays && (
					<div className="one-line-summary-item">
						Duration: <strong>{lengthInDays}</strong> days
					</div>
				)}
				{countries?.length > 0 && (
					<div className="one-line-summary-item">
						{/* <Anchor href={'/'}>
							All {trekOrClimb === 'TREK' ? 'treks' : 'climbs'}
						</Anchor>
						<Anchor
							href={getContinentRoute(
								trekOrClimb,
								trip.jsonData.continent,
							)}
						>
							{trip.jsonData.continent}
						</Anchor> */}
						{pluralize('Country', countries.length)}:{' '}
						<div id="countries">
							{countries.map((isoCode: string, countryIndex: number) => (
								<CountryBadge
									isoCode={isoCode}
									key={countryIndex}
									classes={['trip-page-country-badge']}
								/>
							))}
						</div>
					</div>
				)}
			</div>
			<TopImageGallery trip={trip} onImageClick={lightboxThumbClicked} />
			{description && <HTMLContent html={description} />}
			<TableOfContents trip={trip} />
			<HighLevelSummary trip={trip} />
			<RemainingImagesGallery trip={trip} onImageClick={lightboxThumbClicked} />
		</div>
	)
}

export default Overview
